/* Soul & Grace — Arched Word + Script generator
 * Draws an arched varsity word (solid fill + outline) with a script line under it.
 * Used by the product-page customizer (watermarked preview) and the
 * My Downloads page (clean 300 dpi transparent PNG).
 * Needs opentype.js loaded first.
 */
(function () {
  'use strict';

  var CFG = {
    widthIn: 11,          // finished width of the arched word
    maxHeightIn: 9,       // short words are scaled down so they never get taller than this
    dpi: 300,
    outlinePt: 12,        // word outline
    scriptOutlinePt: 8,   // script outline (thinner so loops don't fill in)
    track: 22,            // letter spacing, font units
    topDrop: 0.07,        // how much the top edge drops at the ends
    botDrop: 0.34,        // how much the bottom edge drops at the ends
    scriptSize: 0.13,     // script font size as a share of the 11" width
    scriptMaxWidth: 0.95, // long script lines shrink to fit this share of the 11" width
    scriptWordGap: 0.22,  // extra space between script words (em)
    scriptGap: 0.17,      // gap between the arch's center bottom and the script
    curveSteps: 16
  };

  var state = { sport: null, scriptFamily: 'SGWildflowerScript', ready: null };

  function load(opts) {
    if (state.ready) return state.ready;
    state.ready = Promise.all([
      new Promise(function (res, rej) {
        opentype.load(opts.sportUrl, function (err, font) { if (err) rej(err); else { state.sport = font; res(); } });
      }),
      new FontFace(state.scriptFamily, 'url(' + opts.scriptUrl + ')').load().then(function (ff) { document.fonts.add(ff); })
    ]);
    return state.ready;
  }

  // keep only characters the varsity font actually has
  function cleanWord(w) {
    w = (w || '').toUpperCase();
    if (!state.sport) return w;
    var out = '';
    for (var i = 0; i < w.length; i++) {
      var ch = w[i];
      if (ch === ' ' || state.sport.charToGlyphIndex(ch) > 0) out += ch;
    }
    return out.replace(/\s+/g, ' ').trim();
  }

  // flatten glyph outlines into polygons (font units, y up)
  function wordPolys(word) {
    var font = state.sport, polys = [], x = 0, top = -Infinity, bot = Infinity, S = CFG.curveSteps;
    for (var i = 0; i < word.length; i++) {
      var g = font.charToGlyph(word[i]);
      var p = g.getPath(0, 0, font.unitsPerEm); // y down, size = upm -> 1:1 font units
      var cur = null, last = null;
      p.commands.forEach(function (c) {
        if (c.type === 'M') { cur = [[c.x + x, -c.y]]; polys.push(cur); last = [c.x, c.y]; }
        else if (c.type === 'L') { cur.push([c.x + x, -c.y]); last = [c.x, c.y]; }
        else if (c.type === 'Q') {
          for (var s = 1; s <= S; s++) { var t = s / S, m = 1 - t;
            cur.push([m*m*last[0] + 2*m*t*c.x1 + t*t*c.x + x, -(m*m*last[1] + 2*m*t*c.y1 + t*t*c.y)]); }
          last = [c.x, c.y];
        } else if (c.type === 'C') {
          for (var s2 = 1; s2 <= S; s2++) { var t2 = s2 / S, m2 = 1 - t2;
            cur.push([m2*m2*m2*last[0] + 3*m2*m2*t2*c.x1 + 3*m2*t2*t2*c.x2 + t2*t2*t2*c.x + x,
                      -(m2*m2*m2*last[1] + 3*m2*m2*t2*c.y1 + 3*m2*t2*t2*c.y2 + t2*t2*t2*c.y)]); }
          last = [c.x, c.y];
        }
      });
      var bb = g.getBoundingBox();
      if (bb.y2 > bb.y1) { top = Math.max(top, bb.y2); bot = Math.min(bot, bb.y1); }
      x += g.advanceWidth + CFG.track;
    }
    return { polys: polys, top: top, bot: bot };
  }

  /* Render to a canvas.
   * d = {word, script, fill, outline, scriptColor}
   * o = {pxPerIn (default 300), watermark (text or false)}
   */
  function render(canvas, d, o) {
    o = o || {};
    var ppi = o.pxPerIn || CFG.dpi, pt = ppi / 72;
    var word = cleanWord(d.word);
    var ctx = canvas.getContext('2d');
    if (!word) { canvas.width = 10; canvas.height = 10; return canvas; }

    var w = wordPolys(word);
    var xs = [], i, j;
    w.polys.forEach(function (pl) { pl.forEach(function (p) { xs.push(p[0]); }); });
    var x0 = Math.min.apply(null, xs), x1 = Math.max.apply(null, xs);
    var xc = (x0 + x1) / 2, hw = (x1 - x0) / 2 || 1, H = w.top - w.bot, W = x1 - x0;
    var archH = H * (1 + CFG.botDrop);
    var k = Math.min(CFG.widthIn * ppi / W, CFG.maxHeightIn * ppi / archH); // font units -> px
    var Wpx = W * k, Hpx = archH * k;

    function warp(px, py) {
      var u = (px - xc) / hw, yn = (w.top - py) / H;
      var t = CFG.topDrop * H * u * u, b = H + CFG.botDrop * H * u * u;
      return [(px - x0) * k, (t + yn * (b - t)) * k];
    }

    // script measurements
    var script = (d.script || '').trim().replace(/\s+/g, ' '), sSize = 0, sAsc = 0, sDesc = 0, sTop = 0, sW = 0, sWords = [];
    var fullW = CFG.widthIn * ppi;
    function measureScript(size) {
      ctx.font = size + 'px ' + state.scriptFamily;
      var gap = ctx.measureText(' ').width + CFG.scriptWordGap * size, x = 0, left = Infinity, right = -Infinity, asc = 0, desc = 0;
      var words = script.split(' ').map(function (t) {
        var m = ctx.measureText(t);
        left = Math.min(left, x - m.actualBoundingBoxLeft); right = Math.max(right, x + m.actualBoundingBoxRight);
        asc = Math.max(asc, m.actualBoundingBoxAscent); desc = Math.max(desc, m.actualBoundingBoxDescent);
        var r = { t: t, x: x }; x += m.width + gap; return r;
      });
      return { words: words, left: left, width: right - left, asc: asc, desc: desc };
    }
    if (script) {
      var m1 = measureScript(100);
      sSize = Math.min(CFG.scriptSize * fullW, 100 * CFG.scriptMaxWidth * fullW / m1.width);
      var ms = measureScript(sSize);
      sWords = ms.words; sW = ms.width; sAsc = ms.asc; sDesc = ms.desc;
      sWords.forEach(function (wd) { wd.x -= ms.left; });
      sTop = H * k + CFG.scriptGap * Hpx;
    }

    var pad = Math.ceil(CFG.outlinePt * pt + 6);
    var contentH = Math.max(Hpx, script ? sTop + sAsc + sDesc + CFG.scriptOutlinePt * pt : 0);
    var innerW = Math.max(Wpx, sW), wordX = (innerW - Wpx) / 2;
    canvas.width = Math.ceil(innerW + 2 * pad);
    canvas.height = Math.ceil(contentH + 2 * pad);
    ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.translate(pad + wordX, pad);

    // word path
    var path = new Path2D();
    w.polys.forEach(function (pl) {
      for (j = 0; j < pl.length; j++) { var q = warp(pl[j][0], pl[j][1]); if (j) path.lineTo(q[0], q[1]); else path.moveTo(q[0], q[1]); }
      path.closePath();
    });
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.fillStyle = ctx.strokeStyle = d.outline;
    ctx.lineWidth = CFG.outlinePt * pt * 2;
    ctx.stroke(path); ctx.fill(path, 'nonzero');
    ctx.fillStyle = d.fill;
    ctx.fill(path, 'nonzero');

    // script (drawn word by word so spaces stay open between swashes)
    if (script) {
      ctx.setTransform(1, 0, 0, 1, pad + (innerW - sW) / 2, pad);
      ctx.font = sSize + 'px ' + state.scriptFamily;
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      var base = sTop + sAsc;
      ctx.lineWidth = CFG.scriptOutlinePt * pt * 2;
      ctx.strokeStyle = ctx.fillStyle = d.outline;
      sWords.forEach(function (wd) { ctx.strokeText(wd.t, wd.x, base); ctx.fillText(wd.t, wd.x, base); });
      ctx.fillStyle = d.scriptColor;
      sWords.forEach(function (wd) { ctx.fillText(wd.t, wd.x, base); });
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    if (o.watermark) drawWatermark(canvas, o.watermark);
    return canvas;
  }

  // canvas -> PNG blob tagged as 300 dpi (pHYs chunk)
  function crc32(buf) {
    var c, crc = 0xffffffff, n, k2, table = crc32.t;
    if (!table) { table = crc32.t = []; for (n = 0; n < 256; n++) { c = n; for (k2 = 0; k2 < 8; k2++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; table[n] = c >>> 0; } }
    for (n = 0; n < buf.length; n++) crc = table[(crc ^ buf[n]) & 0xff] ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
  }
  function withDpi(bytes, dpi) {
    var ppm = Math.round(dpi / 0.0254);
    var chunk = new Uint8Array(21), dv = new DataView(chunk.buffer);
    dv.setUint32(0, 9); chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
    dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
    dv.setUint32(17, crc32(chunk.subarray(4, 17)));
    var at = 33; // after signature (8) + IHDR (25)
    var out = new Uint8Array(bytes.length + 21);
    out.set(bytes.subarray(0, at)); out.set(chunk, at); out.set(bytes.subarray(at), at + 21);
    return out;
  }
  function toPngBlob(canvas, dpi) {
    return new Promise(function (res) {
      canvas.toBlob(function (b) {
        b.arrayBuffer().then(function (ab) { res(new Blob([withDpi(new Uint8Array(ab), dpi || CFG.dpi)], { type: 'image/png' })); });
      }, 'image/png');
    });
  }
  function download(d, filename) {
    var c = document.createElement('canvas');
    render(c, d, { pxPerIn: CFG.dpi });
    return toPngBlob(c, CFG.dpi).then(function (blob) {
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename || (cleanWord(d.word) || 'design').replace(/\s+/g, '_') + '_300dpi.png';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
    });
  }

  // ---- sweatshirt mockup preview ----
  var MOCK = { centerX: 0.47, top: 0.235, width: 0.36 }; // chest placement, as shares of the mockup
  var mockImgs = {};
  function loadImage(url) {
    if (!mockImgs[url]) mockImgs[url] = new Promise(function (res, rej) {
      var img = new Image(); img.onload = function () { res(img); }; img.onerror = rej; img.src = url;
    });
    return mockImgs[url];
  }
  function drawWatermark(canvas, text) {
    var ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalAlpha = 0.28; ctx.fillStyle = '#ffffff'; ctx.strokeStyle = '#555555';
    ctx.lineWidth = Math.max(1, canvas.width / 900);
    var fs = canvas.width / 16;
    ctx.font = 'bold ' + fs + 'px sans-serif'; ctx.textAlign = 'center';
    ctx.translate(canvas.width / 2, canvas.height / 2); ctx.rotate(-0.35);
    for (var i = -2; i <= 2; i++) { ctx.fillText(text, 0, i * fs * 2.6); ctx.strokeText(text, 0, i * fs * 2.6); }
    ctx.restore();
  }
  // d = design choices; o = {img (loaded mockup), size (px, default 1000), watermark}
  function renderMockup(canvas, d, o) {
    var S = o.size || 1000, img = o.img;
    canvas.width = S; canvas.height = S;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, S, S);
    var art = document.createElement('canvas');
    var ppi = MOCK.width * S / CFG.widthIn;
    render(art, d, { pxPerIn: ppi });
    if (art.width > 10) {
      var pad = Math.ceil(CFG.outlinePt * ppi / 72 + 6);
      ctx.save(); ctx.globalAlpha = 0.97;
      ctx.drawImage(art, MOCK.centerX * S - art.width / 2, MOCK.top * S - pad);
      ctx.restore();
    }
    if (o.watermark) drawWatermark(canvas, o.watermark);
    return canvas;
  }

  // "Navy (#1F2F5C)" -> "#1F2F5C"
  function hexFrom(v) { var m = /#[0-9a-f]{6}/i.exec(v || ''); return m ? m[0] : (v || '#000000'); }

  window.SGArch = { CFG: CFG, MOCK: MOCK, load: load, loadImage: loadImage, renderMockup: renderMockup, render: render, toPngBlob: toPngBlob, download: download, cleanWord: cleanWord, hexFrom: hexFrom };
})();
